/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 * Utilities for converting between Deposit and Book Value schedule types
 */

import {
  PaymentRow,
  PaymentRowType,
  Scenario,
  ScheduleType,
} from '../../shared/types'

export const MAX_DECIMAL_PRECISION = 3

/**
 * Returns the effective payout value for a payment row based on the feature flag.
 * Centralises the enablePartialMaturitiesByDollarAmount ternary so callers
 * don't need to repeat it and risk reading the wrong field.
 */
export function getRowPayoutValue(
  row: PaymentRow,
  enableDollarAmount: boolean
): number {
  return enableDollarAmount
    ? (row.payoutAmount ?? 0)
    : (row.payoutPercentage ?? 0)
}

/**
 * Normalizes a payment row's value to a percentage of the deposit amount.
 * When enableDollarAmount is false, returns the row's payoutPercentage directly.
 * When enabled, checks row.inputType: PERCENTAGE rows return payoutAmount as-is,
 * DOLLAR rows are converted via (payoutAmount / depositAmount) * 100.
 */
export function normalizePaymentRowToPercentage(
  row: PaymentRow,
  enableDollarAmount: boolean,
  depositAmount: number
): number {
  if (!enableDollarAmount) {
    return row.payoutPercentage ?? 0
  }
  const rawValue = row.payoutAmount ?? 0
  const isPercentage =
    (row.inputType ?? PaymentRowType.PERCENTAGE) === PaymentRowType.PERCENTAGE
  if (isPercentage) {
    return rawValue
  }
  return depositAmount > 0 ? (rawValue / depositAmount) * 100 : 0
}

/**
 * Comparator for sorting payment rows by date in ascending order.
 * Null dates sort to the end.
 */
export function paymentDateComparator(a: PaymentRow, b: PaymentRow): number {
  if (a.paymentDate == null && b.paymentDate == null) return 0
  if (a.paymentDate == null) return 1
  if (b.paymentDate == null) return -1
  return (
    new Date(a.paymentDate as string).getTime() -
    new Date(b.paymentDate as string).getTime()
  )
}

// Store original deposit values when converting to Book Value
const depositValuesMap = new Map<string, PaymentRow[]>()

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 *
 * Converts all scenarios from one schedule type to another.
 *
 * @param scenarios - Array of scenarios to convert
 * @param newScheduleType - Target schedule type
 * @returns Converted scenarios
 */
export function convertScenarios(
  scenarios: Scenario[],
  newScheduleType: ScheduleType,
  enablePartialMaturitiesByDollarAmount: boolean = false
): Scenario[] {
  // Convert all scenarios
  const convertedScenarios = scenarios.map((scenario) => {
    const hasAnyDollarAmount = scenario.paymentSchedule.some(
      (row) =>
        (row.inputType ?? PaymentRowType.PERCENTAGE) === PaymentRowType.DOLLAR
    )

    if (newScheduleType === ScheduleType.BOOK_VALUE) {
      // Converting Deposit → Book Value
      // Store current deposit values for restoration
      depositValuesMap.set(
        scenario.id,
        JSON.parse(JSON.stringify(scenario.paymentSchedule)) as PaymentRow[]
      )

      if (hasAnyDollarAmount) {
        // If any dollar amounts exist, only convert last chronological row to 100%
        // Sort by date to find the last chronological row
        const lastChronologicalIndex =
          scenario.paymentSchedule
            .map((row, index) => ({ index, date: row.paymentDate }))
            .sort((a, b) => {
              if (a.date == null && b.date == null) return 0
              if (a.date == null) return -1
              if (b.date == null) return 1
              return new Date(a.date).getTime() - new Date(b.date).getTime()
            })
            .at(-1)?.index ?? scenario.paymentSchedule.length - 1

        return {
          ...scenario,
          paymentSchedule: scenario.paymentSchedule.map((row, index) => {
            if (index === lastChronologicalIndex) {
              return {
                ...row,
                payoutAmount: enablePartialMaturitiesByDollarAmount ? 100 : 0,
                payoutPercentage: enablePartialMaturitiesByDollarAmount
                  ? 0
                  : 100,
                inputType: PaymentRowType.PERCENTAGE,
              }
            }
            return row
          }),
        }
      } else {
        // All percentages - do normal conversion
        // Sort by date to ensure chronological order for conversion math
        const sortedIndices = scenario.paymentSchedule
          .map((row, index) => ({ index, date: row.paymentDate }))
          .sort((a, b) => {
            if (a.date == null || b.date == null) return 0
            return new Date(a.date).getTime() - new Date(b.date).getTime()
          })
          .map((item) => item.index)

        const sortedPercentages = sortedIndices.map((i) =>
          getRowPayoutValue(
            scenario.paymentSchedule[i],
            enablePartialMaturitiesByDollarAmount
          )
        )
        const sortedConverted = depositToBookValue(sortedPercentages)
        // Force last chronological value to always be 100 for Book Value
        if (sortedConverted.length > 0) {
          sortedConverted[sortedConverted.length - 1] = 100
        }

        // Map converted values back to original array positions
        const convertedPercentages: number[] = new Array<number>(
          scenario.paymentSchedule.length
        ).fill(0)
        sortedIndices.forEach((originalIndex, sortedIndex) => {
          convertedPercentages[originalIndex] = sortedConverted[sortedIndex]
        })

        // Determine which original index is the last chronological row
        const lastChronologicalIndex = sortedIndices[sortedIndices.length - 1]

        return {
          ...scenario,
          paymentSchedule: scenario.paymentSchedule.map((row, index) => {
            const isLastChronological = index === lastChronologicalIndex
            const convertedValue =
              convertedPercentages[index] ??
              getRowPayoutValue(row, enablePartialMaturitiesByDollarAmount)
            return {
              ...row,
              payoutAmount: enablePartialMaturitiesByDollarAmount
                ? convertedValue
                : 0,
              payoutPercentage: enablePartialMaturitiesByDollarAmount
                ? 0
                : convertedValue,
              // Force last chronological row to PERCENTAGE mode in Book Value
              inputType: isLastChronological
                ? PaymentRowType.PERCENTAGE
                : row.inputType,
            }
          }),
        }
      }
    } else {
      // Converting Book Value → Deposit
      const currentBookValue = scenario.paymentSchedule
      const storedDepositValues = depositValuesMap.get(scenario.id)

      if (
        storedDepositValues != null &&
        storedDepositValues.length === currentBookValue.length
      ) {
        // Restore deposit structure, but preserve any dollar amount edits from Book Value
        const restoredSchedule = storedDepositValues.map(
          (depositRow, index) => {
            const currentRow = currentBookValue[index]
            const isDollarAmount =
              (depositRow.inputType ?? PaymentRowType.PERCENTAGE) ===
              PaymentRowType.DOLLAR

            if (isDollarAmount) {
              // Preserve dollar amount edits made in Book Value mode
              const amount = getRowPayoutValue(
                currentRow,
                enablePartialMaturitiesByDollarAmount
              )
              return {
                ...depositRow,
                payoutAmount: enablePartialMaturitiesByDollarAmount
                  ? amount
                  : 0,
                payoutPercentage: enablePartialMaturitiesByDollarAmount
                  ? 0
                  : amount,
              }
            }
            return depositRow
          }
        )

        return {
          ...scenario,
          paymentSchedule: restoredSchedule,
        }
      } else {
        // No stored deposit values - do normal conversion
        // Sort by date to ensure chronological order for conversion math
        const sortedIndices = currentBookValue
          .map((row, index) => ({ index, date: row.paymentDate }))
          .sort((a, b) => {
            if (a.date == null || b.date == null) return 0
            return new Date(a.date).getTime() - new Date(b.date).getTime()
          })
          .map((item) => item.index)

        const sortedPercentages = sortedIndices.map((i) =>
          getRowPayoutValue(
            currentBookValue[i],
            enablePartialMaturitiesByDollarAmount
          )
        )
        const sortedConverted = bookValueToDeposit(sortedPercentages)
        // Force last chronological value to be 100 - sum of others for Deposit
        if (sortedConverted.length > 1) {
          const sum = sortedConverted
            .slice(0, -1)
            .reduce((acc, val) => acc + val, 0)
          sortedConverted[sortedConverted.length - 1] = 100 - sum
        }

        // Map converted values back to original array positions
        const convertedPercentages: number[] = new Array<number>(
          currentBookValue.length
        ).fill(0)
        sortedIndices.forEach((originalIndex, sortedIndex) => {
          convertedPercentages[originalIndex] = sortedConverted[sortedIndex]
        })

        return {
          ...scenario,
          paymentSchedule: currentBookValue.map((row, index) => {
            const convertedValue =
              convertedPercentages[index] ??
              getRowPayoutValue(row, enablePartialMaturitiesByDollarAmount)
            return {
              ...row,
              payoutAmount: enablePartialMaturitiesByDollarAmount
                ? convertedValue
                : 0,
              payoutPercentage: enablePartialMaturitiesByDollarAmount
                ? 0
                : convertedValue,
            }
          }),
        }
      }
    }
  })

  return convertedScenarios
}

/**
 * Convert Deposit percentages to Book Value (cumulative) percentages
 *
 * Formula:
 *   BV(0) = D(0)
 *   BV(n) = 100 * D(n) / (100 - ∑(D(i) for i=0 to n-1))
 *
 * Explanation: Each book value represents what percentage of the remaining principal
 * this payment represents. For example, if 50% was already paid, the next 25% deposit
 * represents 25/50 = 50% of the remaining book value.
 *
 * Note: All values are calculated, including the last. Use convertScenarios() to force
 * the last value to exactly 100.
 *
 * @param deposits - Array of deposit percentages (e.g., [50, 50] or [25, 25, 25, 25])
 * @returns Array of book value percentages with calculated (not forced) values
 */
export function depositToBookValue(deposits: number[]): number[] {
  if (deposits.length === 0) return []
  if (deposits.length === 1) return [100]

  const result: number[] = []
  let cumulativeSum = 0

  for (let n = 0; n < deposits.length; n++) {
    if (n === 0) {
      // BV(0) = D(0)
      const sourcePrecision = getDecimalPlaces(deposits[0])
      result.push(roundToPrecision(deposits[0], sourcePrecision))
    } else {
      // Calculate BV for all remaining values including last
      // BV(n) = 100 * D(n) / (100 - cumulative sum)
      const bvPercentage = 100 * (deposits[n] / (100 - cumulativeSum))

      const sourcePrecision = getDecimalPlaces(deposits[n])
      result.push(roundToPrecision(bvPercentage, sourcePrecision))
    }

    cumulativeSum += deposits[n]
  }

  return result
}

/**
 * Convert Book Value (cumulative) percentages to Deposit percentages
 * Formula:
 *   D(0) = BV(0)
 *   D(n) = BV(n) · (1 - ∑(D(i) for i=0 to n-1))
 *
 * Note: Last value is calculated, not forced. Use convertScenarios to force constraints.
 */
export function bookValueToDeposit(bookValues: number[]): number[] {
  if (bookValues.length === 0) return []
  if (bookValues.length === 1) return [100]

  const result: number[] = []

  for (let n = 0; n < bookValues.length; n++) {
    if (n === 0) {
      // D(0) = BV(0)
      const sourcePrecision = getDecimalPlaces(bookValues[0])
      result.push(roundToPrecision(bookValues[0], sourcePrecision))
    } else {
      // Calculate D for all remaining values including last
      // D(n) = BV(n) · (1 - ∑(D(i) for i=0 to n-1))
      let sumDeposits = 0
      for (let i = 0; i < n; i++) {
        sumDeposits += result[i]
      }

      const depositPercentage = bookValues[n] * (1 - sumDeposits / 100)

      const sourcePrecision = getDecimalPlaces(bookValues[n])
      result.push(roundToPrecision(depositPercentage, sourcePrecision))
    }
  }

  return result
}

/**
 * Check if converting from Book Value to Deposit will result in totals exceeding 100%
 * This happens when dollar amounts are edited in Book Value mode
 *
 * @param scenarios - Array of scenarios in Book Value mode
 * @param depositAmount - Deposit amount for dollar amount conversion
 * @returns True if any scenario will exceed 100% after conversion
 */
export function willExceed100AfterConversion(
  scenarios: Scenario[],
  depositAmount?: number
): boolean {
  if (scenarios.length === 0 || depositAmount == null || depositAmount <= 0)
    return false

  for (const scenario of scenarios) {
    const currentBookValue = scenario.paymentSchedule
    const storedDepositValues = depositValuesMap.get(scenario.id)

    if (
      storedDepositValues == null ||
      storedDepositValues.length !== currentBookValue.length
    ) {
      continue
    }

    // Simulate the restoration with dollar amount edits
    const restoredSchedule = storedDepositValues.map((depositRow, index) => {
      const currentRow = currentBookValue[index]
      const isDollarAmount =
        (depositRow.inputType ?? PaymentRowType.PERCENTAGE) ===
        PaymentRowType.DOLLAR

      if (isDollarAmount) {
        return {
          ...depositRow,
          payoutAmount: currentRow.payoutAmount,
          payoutPercentage: currentRow.payoutPercentage,
        }
      }
      return depositRow
    })

    // Calculate total percentage (converting dollar amounts)
    const total = restoredSchedule.reduce((sum, row) => {
      const isPercentage =
        (row.inputType ?? PaymentRowType.PERCENTAGE) ===
        PaymentRowType.PERCENTAGE
      const amount = row.payoutAmount ?? 0

      if (isPercentage) {
        return sum + amount
      } else {
        // Convert dollar amount to percentage
        return sum + (amount / depositAmount) * 100
      }
    }, 0)

    if (total > 100.001) {
      return true
    }
  }

  return false
}

/**
 * Written by Windsurf using model: Claude Sonnet 4.5 (Thinking)
 *
 * Check if scenarios have rounding errors when doing a round-trip conversion.
 * Performs: scenarios (type A) → type B → type A, then compares with original.
 * Uses exact equality comparison (no epsilon tolerance).
 *
 * @param scenarios - Array of scenarios to check
 * @param currentScheduleType - Current schedule type of the scenarios
 * @returns True if round-trip conversion doesn't match original
 */
export function scenariosHaveRoundingError(
  scenarios: Scenario[],
  currentScheduleType: ScheduleType,
  enablePartialMaturitiesByDollarAmount: boolean = false
): boolean {
  if (scenarios.length === 0) return false

  // Determine target type for conversion
  const targetType =
    currentScheduleType === ScheduleType.BOOK_VALUE
      ? ScheduleType.DEPOSIT
      : ScheduleType.BOOK_VALUE

  // Round-trip: convert to target type and back
  const convertedOnce = convertScenarios(
    scenarios,
    targetType,
    enablePartialMaturitiesByDollarAmount
  )
  const convertedBack = convertScenarios(
    convertedOnce,
    currentScheduleType,
    enablePartialMaturitiesByDollarAmount
  )

  // Compare with original
  for (let i = 0; i < scenarios.length; i++) {
    const originalSchedule = scenarios[i].paymentSchedule
    const roundTripSchedule = convertedBack[i].paymentSchedule

    if (originalSchedule.length !== roundTripSchedule.length) return true

    for (let j = 0; j < originalSchedule.length; j++) {
      const originalValue = getRowPayoutValue(
        originalSchedule[j],
        enablePartialMaturitiesByDollarAmount
      )
      const roundTripValue = getRowPayoutValue(
        roundTripSchedule[j],
        enablePartialMaturitiesByDollarAmount
      )
      if (originalValue !== roundTripValue) {
        return true
      }
    }
  }

  return false
}

/**
 * Get the number of decimal places in a number, after normalizing to remove
 * floating-point artifacts
 */
function getDecimalPlaces(value: number): number {
  // First normalize the value to MAX_DECIMAL_PRECISION to remove floating-point artifacts
  const normalized = normalizeFloat(value)
  const str = normalized.toString()
  const decimalIndex = str.indexOf('.')
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1
}

/**
 * Normalize a floating-point number by rounding to MAX_DECIMAL_PRECISION + 2
 * to remove floating-point arithmetic artifacts
 */
function normalizeFloat(value: number): number {
  const extraPrecision = 2
  const multiplier = Math.pow(10, MAX_DECIMAL_PRECISION + extraPrecision)
  return Math.round(value * multiplier) / multiplier
}

/**
 * Round a number to specified decimal places, capped at MAX_DECIMAL_PRECISION
 */
function roundToPrecision(value: number, sourcePrecision: number): number {
  const precision = Math.min(sourcePrecision, MAX_DECIMAL_PRECISION)
  const multiplier = Math.pow(10, precision)
  return Math.round(value * multiplier) / multiplier
}
